import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const SUBSCRIBE_FORM_ID = '51chigua-subscribe';
const FORM_SUBMIT_URL = 'http://dingzhishi.cn/';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim()) {
      setSubmitStatus('success');
      setEmail('');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('email', email.trim());
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setSubmitStatus('error');
        setFormError(serverMsg || '订阅失败，请稍后重试');
      }
    } catch {
      setSubmitStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-8 py-14 md:py-16">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <Link to="/" className="flex items-center gap-2 mb-4 whitespace-nowrap">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-fire-fill text-accent-500 text-xl"></i>
                </span>
                <span className="font-heading text-lg text-background-50">
                  {t('common_siteName')}
                </span>
              </Link>
              <p className="text-foreground-400 text-sm leading-relaxed">
                {t('common_footer_brandIntro')}
              </p>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-background-50 text-sm font-medium mb-4">
                {t('common_footer_categories')}
              </h4>
              <div className="flex flex-col gap-2">
                <Link to="/category/star-scandal" className="text-foreground-400 text-sm hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  {t('common_nav_starScandal')}
                </Link>
                <Link to="/category/influencer-gossip" className="text-foreground-400 text-sm hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  {t('common_nav_influencerGossip')}
                </Link>
                <Link to="/category/black-material" className="text-foreground-400 text-sm hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  {t('common_nav_blackMaterial')}
                </Link>
              </div>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-background-50 text-sm font-medium mb-4">
                {t('common_footer_quickLinks')}
              </h4>
              <div className="flex flex-col gap-2">
                <Link to="/" className="text-foreground-400 text-sm hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  {t('common_nav_home')}
                </Link>
                <Link to="/about" className="text-foreground-400 text-sm hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  {t('common_nav_about')}
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <h4 className="text-background-50 text-sm font-medium mb-4">
                {t('common_footer_subscribe')}
              </h4>
              {submitStatus === 'success' ? (
                <p className="text-accent-400 text-sm">订阅成功！感谢关注~</p>
              ) : (
                <form id={SUBSCRIBE_FORM_ID} onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('common_footer_emailPlaceholder')}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-foreground-800 text-background-50 text-sm placeholder:text-foreground-500 border border-foreground-700 focus:outline-none focus:border-primary-500 transition-colors"
                    required
                  />
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-anti-spam-field"
                  />
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {t('common_footer_subscribe')}
                  </button>
                </form>
              )}
              {submitStatus === 'error' && (
                <p className="text-accent-500 text-xs mt-2">{formError}</p>
              )}
              <div className="flex items-center gap-4 mt-6">
                <span className="text-foreground-400 text-xs whitespace-nowrap">
                  {t('common_footer_followUs')}
                </span>
                <a href="#" className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-background-50 transition-colors cursor-pointer" aria-label="微博">
                  <i className="ri-weibo-line text-lg"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-background-50 transition-colors cursor-pointer" aria-label="微信">
                  <i className="ri-wechat-line text-lg"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-background-50 transition-colors cursor-pointer" aria-label="抖音">
                  <i className="ri-tiktok-line text-lg"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-foreground-800 mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-foreground-500 text-xs">
              &copy; {new Date().getFullYear()} {t('common_footer_copyright')}. {t('common_footer_rightsReserved')}
            </p>
            <div className="flex items-center gap-4 text-foreground-500 text-xs">
              <a href="#" className="hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">隐私政策</a>
              <a href="#" className="hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">用户协议</a>
              <a href="#" className="hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">免责声明</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}